import { useEffect } from 'react';

const useScrollSpy = (sectionIds, offset = 0) => {
  useEffect(() => {
    // Track if we're manually scrolling to a section
    let isManualScroll = false;
    let manualScrollTimeout;

    const observerOptions = {
      root: null,
      rootMargin: `-${offset}px 0px -70% 0px`,
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      // Skip if we're in a manual scroll operation
      if (isManualScroll) return;

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const id = entry.target.id;
          updateActiveState(`#${id}`);
        }
      });
    }, observerOptions);

    // Function to update active state and URL hash
    const updateActiveState = (hash) => {
      if (hash !== window.location.hash) {
        window.history.replaceState(null, '', hash);
      }
      updateActiveLink(hash);
    };

    // Handle hash changes (both from scroll and manual navigation)
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash) {
        updateActiveLink(hash);
      }
    };

    // Handle anchor clicks
    const handleAnchorClick = (e) => {
      const anchor = e.target.closest("a[href^='#']");
      if (anchor) {
        e.preventDefault();
        const hash = anchor.getAttribute('href');
        if (hash) {
          const targetEl = document.getElementById(hash.substring(1));
          if (targetEl) {
            isManualScroll = true;

            // Scroll to the element
            targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });

            // Update active state immediately
            updateActiveState(hash);

            // Reset manual scroll flag after a delay
            clearTimeout(manualScrollTimeout);
            manualScrollTimeout = setTimeout(() => {
              isManualScroll = false;
            }, 1000);
          }
        }
      }
    };

    // Check if an element is at the bottom of the page
    const isElementAtBottom = (el) => {
      const rect = el.getBoundingClientRect();
      return (
        rect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight)
      );
    };

    // Observe all sections
    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) {
        // If element is already at the bottom on load, make it active
        if (isElementAtBottom(el) && !window.location.hash) {
          updateActiveState(`#${id}`);
        }
        observer.observe(el);
      }
    });

    // Set up event listeners
    window.addEventListener('hashchange', handleHashChange);
    document.addEventListener('click', handleAnchorClick);

    // Initial state setup
    handleHashChange();

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      document.removeEventListener('click', handleAnchorClick);
      observer.disconnect();
      clearTimeout(manualScrollTimeout);
    };
  }, [sectionIds, offset]);
};

const updateActiveLink = (hash) => {
  document.querySelectorAll("a[href^='#']").forEach((anchor) => {
    anchor.classList.remove('active');
    if (anchor.getAttribute('href') === hash) {
      anchor.classList.add('active');
    }
  });
};

export default useScrollSpy;
